import { all, call, put, select } from 'redux-saga/effects';

import { waitForAndGetFlagValuesSaga } from '@/core/flags/sagas/waitForAndGetFlagValues.saga';
import { getOrCreateVisitorId } from '@/core/tracking/utils/visitorId';
import { authSelector } from '@/customerManagement/customerManagement.selectors';
import { AlgoliaSearchInsights } from '@/productDiscovery/algolia/searchInsights/AlgoliaSearchInsights';
import { StringABTests as SearchABTests } from '@/productDiscovery/Listings/pages/Search/constants';

import { AlgoliaQueriesIndexes } from '../constants';
import {
  getAutoCompleteSuggestionsFailed,
  getAutoCompleteSuggestionsSuccess,
  type GetAutoCompleteSuggestions,
} from '../HeaderLeonidas.searchBar.actions';
import type {
  BestCategorySuggestionSearchResults,
  SuggestionsSearchResults,
} from '../interfaces/algolia';
import {
  createSuggestionsSearchClientInstance,
  searchMultiple,
} from '../services/algolia/proxy';
import { buildSuggestionsSearchParameters } from '../services/algolia/searchParameters';

function extractAdditionalSuggestions({
  searchResults,
  hasBestCategory,
}: {
  searchResults: SuggestionsSearchResults;
  hasBestCategory: boolean;
}): {
  bestCategory: BestCategorySuggestionSearchResults;
} {
  let currentIndex = Math.max(
    ...Object.values(AlgoliaQueriesIndexes.autoComplete),
  );
  let bestCategory;
  if (hasBestCategory) {
    currentIndex += 1;
    bestCategory = searchResults[currentIndex];
  }
  return { bestCategory };
}

export function* getSuggestions(
  action: GetAutoCompleteSuggestions,
): Generator<any, any, any> {
  const { query } = action;

  const flags = yield call(waitForAndGetFlagValuesSaga);
  const autoselectReliableBestCategoryEnabled =
    flags[SearchABTests.AUTOSELECT_BEST_CATEGORY];

  try {
    const [{ userId }, visitorId, client] = yield all([
      select(authSelector),
      call(getOrCreateVisitorId),
      call(createSuggestionsSearchClientInstance),
    ]);

    const userToken = userId
      ? yield call(AlgoliaSearchInsights.calculateUserIdHash, String(userId))
      : visitorId;

    const results: SuggestionsSearchResults = yield call(
      searchMultiple,
      client,
      buildSuggestionsSearchParameters({
        query,
        bestCategoryEnabled: autoselectReliableBestCategoryEnabled,
      }),
      userToken,
    );

    const {
      QUERY_SUGGESTIONS_INDEX,
      BRAND_SUGGESTIONS_INDEX,
      CATEGORY_SUGGESTIONS_INDEX,
    } = AlgoliaQueriesIndexes.autoComplete;

    const { bestCategory } = extractAdditionalSuggestions({
      searchResults: results,
      hasBestCategory: autoselectReliableBestCategoryEnabled,
    });

    yield put(
      getAutoCompleteSuggestionsSuccess(
        query,
        {
          queries: results[QUERY_SUGGESTIONS_INDEX],
          brands: results[BRAND_SUGGESTIONS_INDEX],
          categories: results[CATEGORY_SUGGESTIONS_INDEX],
          bestCategory,
        },
        autoselectReliableBestCategoryEnabled,
      ),
    );
  } catch (error) {
    yield put(getAutoCompleteSuggestionsFailed(error as Error, query));
  }
}
